import { useCallback, useEffect, useRef, useState } from 'react'
import { Redirect, useLocation } from 'react-router-dom'
import { CREATE_WORKSPACE_PATH } from 'paths'
import { LegacyAntdModal } from 'components/LegacyAntdModal/LegacyAntdModal'

import { Button } from 'components/Button/Button'
import { CloseButton } from 'components/Buttons/CloseButton'
import { CtaButton } from './CtaButton'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { filterPrivateWorkspaces } from 'utils/workspaceUtils'
import { ReactComponent as UpArrowIcon } from 'assets/icons/arrowUp16.svg'
import { useAuth } from 'hooks/UseAuth'
import { useFolder } from 'hooks/queries/folders/useFolder'
import { useFoldersAndFiles } from 'hooks/queries/useFoldersAndFiles'
import { useWorkspace } from 'hooks/useWorkspace'
import { NavSortOrder, AncestorProject, AlmanacFile as File, HandbookItem, MinimalFile } from 'types'
import { FolderClient } from 'api/clients/FolderClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { handleErrorResponseWithMessage } from 'api/utils/handleErrorResponseWithMessage'
import { NewFolderInput } from 'components/NewFolderInput/NewFolderInput'
import useIsMounted from 'ismounted'
import ConfirmationModalStore from 'stores/ConfirmationModalStore'

import { Breadcrumb } from '../Breadcrumb/Breadcrumb'
import { LoadingState } from '../LoadingState/LoadingState'
import { FolderItem } from '../FolderItem/FolderItem'
import { ReactComponent as NewFolderIcon } from '../../icons/newFolder.svg'
import Styles from './ActionModal.module.scss'

import { getSearchString } from 'utils/urlUtils'
import { isAlmanacCoreWorkspace } from 'utils/workspaceUtils'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Modal as AlmanacUIModal } from 'almanac-ui/Modal'

export interface SubmitProps {
  selectedFolder: AncestorProject | null
  selectedHandbookItem?: HandbookItem
  newWorkspaceId: number
}

export interface ActionModalProps {
  ctaText: string
  disableRoot?: boolean
  titleText?: string
  // The current location of the file
  currentFolder?: AncestorProject | null
  // The initial location of the modal
  initialFolder?: AncestorProject | null
  selectedFiles: MinimalFile[]
  setVisible: (visible: boolean) => void
  isMove: boolean
  submit: ({ selectedFolder, selectedHandbookItem, newWorkspaceId }: SubmitProps) => void
  visible: boolean
  hidePopover?: boolean
  initialWorkspaceId?: number
  skipConfirmation?: boolean
  useAlmanacUI?: boolean
}

const ActionModal = ({
  visible,
  setVisible,
  disableRoot,
  selectedFiles,
  ctaText,
  currentFolder,
  initialFolder,
  submit,
  isMove,
  titleText,
  initialWorkspaceId,
  hidePopover,
  skipConfirmation = false,
  useAlmanacUI,
}: ActionModalProps) => {
  const auth = useAuth()
  const location = useLocation()
  const workspaces = useWorkspaces()
  const privateWorkspaces = filterPrivateWorkspaces(workspaces)
  const defaultWorkspaceID =
    initialWorkspaceId ?? // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    (!!privateWorkspaces.length && !privateWorkspaces.map((ws) => ws.id).includes(auth?.company_id!)
      ? privateWorkspaces[0].id // TODO: Fix this the next time the file is edited.
      : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        auth?.company_id!)

  const [changedWorkspace, setChangedWorkspace] = useState(false)
  const [workspaceId, setWorkspaceId] = useState(defaultWorkspaceID)
  const [topLevelFolder, setTopLevelFolder] = useState<AncestorProject | null>(null)
  const [showNewFolderInput, setShowNewFolderInput] = useState(false)
  const initialLoad = useRef(true)
  const [folderItems, setFolderItems] = useState<File[]>([])
  const [selectedFolder, setSelectedFolder] = useState<AncestorProject | null | undefined>(initialFolder)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [differentWorkspace, setDifferentWorkspace] = useState(false)
  const [fetching, setFetching] = useState<boolean>(true)
  const { isMobile } = useResponsiveDesign()

  const store = ConfirmationModalStore

  const { data: folderData } = useFolder(selectedFiles[0]?.parent_id || '')
  const { data: selectedFolderData } = useFolder(selectedFolder?.id || '')
  const destinationAncestorKeys = selectedFolderData?.folder?.ancestor_keys
  const currentParentTitle = folderData?.folder?.title

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const workspace = useWorkspace(workspaceId)!
  const movingToRoot = !selectedFolder && !topLevelFolder
  const allowRoot = !selectedFiles.length && !disableRoot
  const allSelectedAreFolders = !!selectedFiles.length && selectedFiles.every((file) => file.type === 'folder')
  const allSelectedFilesNested = selectedFiles.filter((f) => !f.parent_id).length === 0
  const allSelectedDirectChildren = selectedFiles.filter((f) => f.parent_id === topLevelFolder?.id).length === 0

  const isMounted = useIsMounted()

  const foldersAndFiles = useFoldersAndFiles(workspaceId)

  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false
      return
    }

    setFetching(true)
    setFolderItems([])
  }, [topLevelFolder, workspaceId, changedWorkspace])

  useEffect(() => {
    if (foldersAndFiles.isFetching || foldersAndFiles.isFetchingNextPage || topLevelFolder) return

    if (foldersAndFiles.isSuccess) {
      const folders = foldersAndFiles.data?.pages?.flatMap((group) =>
        group.data.files.filter((file: File) => file.type === 'folder'),
      )
      setFolderItems(folders)
    } else {
      showErrorMessageToast('Something went wrong, please try again later.')
    }

    setFetching(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foldersAndFiles.isFetching, foldersAndFiles.isFetchingNextPage, foldersAndFiles.isFetched, topLevelFolder])

  const loadNestedFolder = async (folder: AncestorProject | null) => {
    if (!folder) return
    setFetching(true)
    const response = await FolderClient.getNestedFiles(folder?.id, NavSortOrder.NameAsc, workspaceId)

    handleErrorResponseWithMessage()(response)

    if (!isMounted.current) return

    if (response?.type === SUCCESS_RESPONSE_TYPE) {
      const { data } = response
      setFolderItems(data.files)
    }

    setFetching(false)
  }

  const loadItems = (folder: AncestorProject | null) => {
    if (!!folder) loadNestedFolder(folder)
  }

  const loadMore = async () => {
    if (!foldersAndFiles) return
    await foldersAndFiles.fetchNextPage()
  }

  const hasMore = () => {
    if (topLevelFolder) {
      return false
    }
    if (foldersAndFiles) return !!foldersAndFiles.hasNextPage
    return false
  }

  useEffect(() => {
    if (!changedWorkspace) return

    setTopLevelFolder(null)
  }, [changedWorkspace])

  useEffect(() => {
    if (!workspace) return

    if (workspace.guest || workspace.id === 1) {
      const validWorkspace = workspaces.filter((w) => ![workspace.id, 1].includes(w.id))[0]?.id
      if (!validWorkspace) return

      setWorkspaceId(validWorkspace)
    }
  }, [workspaceId, workspaces, workspace])

  useEffect(() => {
    if (!initialFolder) return
    setTopLevelFolder(initialFolder)
  }, [initialFolder])

  useEffect(() => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (!isAlmanacCoreWorkspace(workspace.id)) loadItems(initialFolder!)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setDifferentWorkspace(auth?.company_id !== workspaceId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId])

  const title = selectedFolder?.title || topLevelFolder?.title

  const closeModal = useCallback(() => {
    setFolderItems([])
    setVisible(false)
  }, [setVisible])

  useEffect(() => {
    if (!showConfirmation) return
    setVisible(false)

    store.setState({
      isConfirmationModalOpen: true,
      selectedFolder: selectedFolder || topLevelFolder,
      selectedFiles: selectedFiles,
      differentWorkspace: differentWorkspace,
      movingToRoot: movingToRoot,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      destinationTitle: title!,
      workspaceName: workspace.name,
      currentParentTitle: currentParentTitle,
      destinationKey: selectedFolder?.id || topLevelFolder?.id,
      submit: submit,
      newWorkspaceId: workspaceId,
      destinationIsAChild: !!(
        selectedFiles[0]?.parent_id && destinationAncestorKeys?.includes(selectedFiles[0].parent_id)
      ),
    })
  }, [
    showConfirmation,
    closeModal,
    currentParentTitle,
    differentWorkspace,
    movingToRoot,
    selectedFiles,
    selectedFolder,
    setVisible,
    submit,
    title,
    topLevelFolder,
    workspace.name,
    workspaceId,
    destinationAncestorKeys,
    store,
  ])

  // Any action perfomed in this modal needs a private workspace. If one does not exist ensure the user creates one then is redirected here.
  if (!privateWorkspaces.length) {
    const redirectTo =
      ctaText === 'Copy'
        ? encodeURIComponent(`${location.pathname}?${getSearchString({ copyTemplate: 'true' })}`)
        : encodeURIComponent(`${location.pathname}${location.search}`)

    return <Redirect to={{ pathname: CREATE_WORKSPACE_PATH, search: `?redirect_to=${redirectTo}` }} />
  }

  const differentContext = () => {
    if (!isMove) return false
    if (!!selectedFolder) return selectedFolder.id === currentFolder?.id
    if (!!topLevelFolder) return topLevelFolder.id === currentFolder?.id && !allSelectedDirectChildren
    if (!topLevelFolder && selectedFiles.length === 1 && selectedFiles[0].type === 'folder') return false
    if (allSelectedFilesNested) return false
    return !differentWorkspace
  }

  const invalidFolder = () => {
    if (!!selectedFolder || !selectedFiles.length) return false
    return !!topLevelFolder && !topLevelFolder.has_edit_access
  }

  const onSelectedFolder = (folder: AncestorProject | null) => {
    setSelectedFolder(folder)
  }

  const openSelectedFolder = (folder: AncestorProject | null) => {
    if (!fetching) {
      setTopLevelFolder(folder)
      setSelectedFolder(folder)
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      loadItems(folder!)
    }
  }

  const handleOnPressCta = () => {
    if (!selectedFolder && !allowRoot && !allSelectedAreFolders) return

    if (!isMove || skipConfirmation) {
      submit({ newWorkspaceId: workspaceId, selectedFolder: selectedFolder || topLevelFolder })
      closeModal()
      return
    }

    setShowConfirmation(true)
  }

  const handleOnPressNewFolder = () => {
    setSelectedFolder(topLevelFolder)
    setShowNewFolderInput(true)
  }

  // Exact same content as below, only wrapped in almanac-ui components. The old modal below is left in place
  // for backwards compatibility, since its being used other, more obscure places in the code base
  if (useAlmanacUI && selectedFolder) {
    return (
      <AlmanacUIModal.Root size="md" onPressOutside={closeModal}>
        <AlmanacUIModal.Header
          onClose={closeModal}
          title={
            titleText
              ? titleText
              : `${ctaText} ${selectedFiles.length} ${selectedFiles.length === 1 ? 'item' : 'items'} to...`
          }
        />
        <AlmanacUIModal.Content>
          <Breadcrumb
            selectedFolder={selectedFolder}
            setSelectedFolder={openSelectedFolder}
            topLevelFolder={topLevelFolder}
            setWorkspaceId={setWorkspaceId}
            workspaceId={workspaceId}
            workspaces={privateWorkspaces}
            setChangedWorkspace={setChangedWorkspace}
            setTopLevelFolder={setTopLevelFolder}
            disabled={fetching}
          />
          <div className={Styles.table}>
            <div className={Styles.tableHeader}>
              <span className={Styles.title}>
                Name <UpArrowIcon />
              </span>
              {!isMobile && <span>Last Modified</span>}
              <span>Shared With</span>
            </div>
            <div className={Styles.tableBody}>
              {showNewFolderInput && (
                <NewFolderInput
                  files={folderItems.filter((item) => item.type !== 'folder')}
                  setFiles={setFolderItems}
                  workspaceId={workspaceId}
                  folders={folderItems.filter((item) => item.type === 'folder')}
                  setFolders={setFolderItems}
                  setShowNewFolderInput={setShowNewFolderInput}
                  folderId={topLevelFolder?.id}
                  className={Styles.newFolderInput}
                  cancelable={true}
                  onFolderCreation={openSelectedFolder}
                />
              )}
              {
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line no-nested-ternary
                fetching ? (
                  <LoadingState />
                ) : folderItems?.length && folderItems[0] !== undefined ? (
                  folderItems.map((folder) => (
                    <FolderItem
                      key={folder.id}
                      folderItem={folder}
                      nestedLevel={0}
                      workspaceId={workspaceId}
                      selectedFolder={selectedFolder}
                      setSelectedFolder={onSelectedFolder}
                      openSelectedFolder={openSelectedFolder}
                      setTopLevelFolder={setTopLevelFolder}
                      selectedFiles={selectedFiles}
                      disabled={fetching}
                    />
                  ))
                ) : (
                  <div className={Styles.emptyState}>
                    <div className={Styles.emptyHeader}>This project is empty.</div>
                    <div className={Styles.emptyText}>There's plenty of room here to move in!</div>
                  </div>
                )
              }
            </div>
            <InfiniteScroll hasMore={hasMore()} loadMore={loadMore} />
          </div>
        </AlmanacUIModal.Content>
        <AlmanacUIModal.Footer>
          <AlmanacUIModal.FooterContent slot="left">
            <AlmanacUIModal.FooterButton variant="tertiary" size="lg" onPress={closeModal}>
              Back
            </AlmanacUIModal.FooterButton>
          </AlmanacUIModal.FooterContent>
          <AlmanacUIModal.FooterContent slot="center">
            <AlmanacUIModal.FooterButton
              frontIcon={isMobile ? undefined : NewFolderIcon}
              isDisabled={fetching}
              variant="secondary"
              onPress={handleOnPressNewFolder}
              size="lg"
            >
              New Project
            </AlmanacUIModal.FooterButton>
          </AlmanacUIModal.FooterContent>
          <AlmanacUIModal.FooterContent slot="right">
            <CtaButton
              isMove={isMove}
              movingToTopLevel={movingToRoot}
              title={title}
              ctaText={ctaText}
              hidePopover={!!hidePopover}
              hideTooltip={!!selectedFolder || allSelectedAreFolders || allowRoot}
              isDisabled={differentContext() || invalidFolder()}
              handleOnPressCta={handleOnPressCta}
            />
          </AlmanacUIModal.FooterContent>
        </AlmanacUIModal.Footer>
      </AlmanacUIModal.Root>
    )
  }

  return (
    <>
      {visible ? (
        <LegacyAntdModal
          visible={visible}
          onCancel={closeModal}
          wrapClassName={Styles.actionModalContainer}
          closable={false}
          width={888}
        >
          <div className={Styles.header}>
            <span className={Styles.headerText}>
              {titleText
                ? titleText
                : `${ctaText} ${selectedFiles.length} ${selectedFiles.length === 1 ? 'item' : 'items'} to...`}
            </span>
            <CloseButton onClick={closeModal} />
          </div>
          <div className={Styles.body}>
            <Breadcrumb
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              selectedFolder={selectedFolder!}
              setSelectedFolder={openSelectedFolder}
              topLevelFolder={topLevelFolder}
              setWorkspaceId={setWorkspaceId}
              workspaceId={workspaceId}
              workspaces={privateWorkspaces}
              setChangedWorkspace={setChangedWorkspace}
              setTopLevelFolder={setTopLevelFolder}
              disabled={fetching}
            />
            <div className={Styles.table}>
              <div className={Styles.tableHeader}>
                <span className={Styles.title}>
                  Name <UpArrowIcon />
                </span>
                {!isMobile && <span>Last Modified</span>}
                <span>Shared With</span>
              </div>
              <div className={Styles.tableBody}>
                {showNewFolderInput && (
                  <NewFolderInput
                    files={folderItems.filter((item) => item.type !== 'folder')}
                    setFiles={setFolderItems}
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    workspaceId={workspaceId!}
                    folders={folderItems.filter((item) => item.type === 'folder')}
                    setFolders={setFolderItems}
                    setShowNewFolderInput={setShowNewFolderInput}
                    folderId={topLevelFolder?.id}
                    className={Styles.newFolderInput}
                    cancelable={true}
                    onFolderCreation={openSelectedFolder}
                  />
                )}
                {
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line no-nested-ternary
                  fetching ? (
                    <LoadingState />
                  ) : folderItems?.length && folderItems[0] !== undefined ? (
                    folderItems.map((folder) => (
                      <FolderItem
                        key={folder.id}
                        // TODO: Fix this the next time the file is edited.
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        folderItem={folder!}
                        nestedLevel={0}
                        // TODO: Fix this the next time the file is edited.
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        workspaceId={workspaceId!}
                        // TODO: Fix this the next time the file is edited.
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        selectedFolder={selectedFolder!}
                        setSelectedFolder={onSelectedFolder}
                        openSelectedFolder={openSelectedFolder}
                        setTopLevelFolder={setTopLevelFolder}
                        selectedFiles={selectedFiles}
                        disabled={fetching}
                      />
                    ))
                  ) : (
                    <div className={Styles.emptyState}>
                      <div className={Styles.emptyHeader}>This folder is empty.</div>
                      <div className={Styles.emptyText}>There's plenty of room here to move in!</div>
                    </div>
                  )
                }
              </div>
              <InfiniteScroll hasMore={hasMore()} loadMore={loadMore} />
            </div>
          </div>
          <div className={Styles.footer}>
            <Button
              frontIcon={NewFolderIcon}
              isDisabled={fetching}
              variant="tertiary"
              onPress={handleOnPressNewFolder}
              size="lg"
            >
              New Folder
            </Button>
            <div className={Styles.buttonContainer}>
              <CtaButton
                isMove={isMove}
                movingToTopLevel={movingToRoot}
                title={title}
                ctaText={ctaText}
                hidePopover={!!hidePopover}
                hideTooltip={!!selectedFolder || allSelectedAreFolders || allowRoot}
                isDisabled={differentContext() || invalidFolder()}
                handleOnPressCta={handleOnPressCta}
              />
            </div>
          </div>
        </LegacyAntdModal>
      ) : null}
    </>
  )
}

export default ActionModal
